import { useQuery } from "@tanstack/react-query";
import type {
  QueryClient,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";

import { useAuthStore } from "../auth/store";

// Скоуп кэша TanStack Query.
//
// Почти все данные Baton принадлежат ОРГАНИЗАЦИИ, а не пользователю:
// очередь, задачи, квоты, роли агентов, подключения, настройки. Активную
// организацию можно менять из интерфейса, и одной инвалидации
// после смены недостаточно: `invalidateQueries()` лишь помечает записи
// устаревшими, но не удаляет их — до прихода свежего ответа компонент
// синхронно отдаёт кэш ПРЕЖНЕЙ организации.
//
// Поэтому организация входит в сам ключ кэша: после смены организации у
// запроса другой ключ и кэша по нему нет — показать чужие данные нечем,
// независимо от политики инвалидации.
//
// Правило удерживается структурно, а не вниманием автора нового хука:
// прямой импорт `useQuery` вне этого модуля запрещён линтером
// (`@typescript-eslint/no-restricted-imports` в `eslint.config.js`), так
// что запрос заводится только через одну из обёрток ниже — и скоуп
// приходится выбрать явно.
//
// Чего гейт НЕ делает, чтобы следующий читатель не считал его сильнее,
// чем он есть: он требует выбрать скоуп, но не проверяет, что выбран
// верный — `useUnscopedQuery` на org-данных пройдёт и линт, и типы.
// Мимо правила идут также `eslint-disable`-директива и запись в кэш в
// обход хуков (`queryClient.fetchQuery` / `setQueryData`); и то и другое
// — явное видимое действие, заметное на ревью. Сюда же
// `placeholderData: keepPreviousData` — опция проходит в `useQuery`
// насквозь и вернёт ровно исходный симптом (данные прежнего ключа, то
// есть прежней организации, показаны синхронно); сейчас в коде её нет.
//
// Второе ограничение — источник организации. Ключ строится из
// `identity.orgId`, а это UI-кэш в localStorage (см. `auth/store.ts`);
// активную организацию запроса выбирает сервер по session-cookie. Пока
// стор и сервер расходятся (протухший localStorage, смена организации в
// соседней вкладке) или запрос ушёл за миг до смены, ответ сервера
// ляжет под ключ прежней организации. Поэтому обе точки, где о смене
// узнаёт клиент, зовут `removeOrgScopedQueries`: мутация переключения и
// `<RequireActiveOrg>`, сверяющий стор с `/me`.
//
// Момент сверки задаёт `/me`: он перезапрашивается на перезагрузке
// вкладки, восстановлении связи или монтировании нового потребителя —
// не по возврату фокуса (`refetchOnWindowFocus` выключен глобально) и
// не сам по себе. Вкладка, открытая до смены организации в соседней,
// узнает о ней не сразу.

/** Сегмент ключа для данных, принадлежащих паре (пользователь, организация). */
interface OrgScopeSegment {
  readonly scope: "org";
  readonly userId: string;
  readonly orgId: string;
}

/** Сегмент ключа для данных, принадлежащих пользователю вне организации. */
interface UserScopeSegment {
  readonly scope: "user";
  readonly userId: string;
}

/**
 * Принадлежит ли ключ org-scoped запросу. Форму скоуп-сегмента знает
 * только этот модуль, наружу она не выходит: снаружи доступны операции
 * ниже, а не сам предикат.
 */
function isOrgScopedQueryKey(key: readonly unknown[]): boolean {
  const tail = key.at(-1);
  return (
    typeof tail === "object" &&
    tail !== null &&
    "scope" in tail &&
    tail.scope === "org"
  );
}

/**
 * Снести из кэша все записи организации — их владелец сменился, и
 * доверять им нельзя (`<RequireActiveOrg>`, узнав из `/me`, что стор
 * расходился с сервером).
 */
export function removeOrgScopedQueries(queryClient: QueryClient): void {
  queryClient.removeQueries({
    predicate: (query) => isOrgScopedQueryKey(query.queryKey),
  });
}

/**
 * Обновить всё, КРОМЕ записей организации.
 *
 * В единственной своей точке вызова — мутации переключения — идёт
 * следом за `removeOrgScopedQueries`, так что сегодня различие
 * незаметно: трогать уже нечего. Фильтр — страховка на случай, если
 * снос когда-нибудь уберут или сузят: безфильтровая инвалидация
 * синхронно рефетчит активные запросы, а те до перерендера держат ключ
 * прежней организации, и ответ новой лёг бы под чужой ключ.
 */
export function invalidateNonOrgScopedQueries(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({
    predicate: (query) => !isOrgScopedQueryKey(query.queryKey),
  });
}

/**
 * Опции запроса без `queryKey`/`enabled` — их считает обёртка.
 *
 * `key` — смысловая часть ключа («что запрашиваем»), скоуп-сегмент
 * обёртка добавляет ХВОСТОМ: префиксный `invalidateQueries({ queryKey:
 * ["tasks"] })` продолжает матчить запись любого скоупа.
 *
 * `enabled` — дополнительное условие запроса (введён репо, выбрана роль);
 * обёртка комбинирует его с наличием скоупа через `&&`.
 */
type ScopedQueryOptions<TData> = Omit<
  UseQueryOptions<TData, Error, TData, readonly unknown[]>,
  "queryKey" | "enabled"
> & {
  readonly key: readonly unknown[];
  readonly enabled?: boolean;
};

/**
 * Запрос org-scoped данных — дефолт для всего, что отдаёт backend внутри
 * активной организации.
 *
 * Пока организации нет (org-less сессия, ADR-0038), запрос отключён: без
 * активной организации серверу нечего отдавать, а слать заведомо
 * невалидный запрос — шум.
 */
export function useOrgScopedQuery<TData>(
  options: ScopedQueryOptions<TData>,
): UseQueryResult<TData, Error> {
  const { key, enabled = true, ...rest } = options;
  const queryKey = useOrgScopedQueryKey(key);
  return useQuery<TData, Error, TData, readonly unknown[]>({
    ...rest,
    queryKey: queryKey ?? [...key, null],
    enabled: queryKey !== null && enabled,
  });
}

/**
 * Полный org-scoped ключ кэша для `key` — то же построение, что у
 * `useOrgScopedQuery`, но без самого запроса.
 *
 * Нужен коду, который пишет в кэш запроса напрямую (`setQueryData` после
 * мутации): запись в полный ключ текущей организации, а не префиксный
 * `setQueriesData`, который задевает записи любой организации с тем же
 * `key`. `null` — активной организации нет (org-less сессия), писать
 * в кэш нечего.
 */
export function useOrgScopedQueryKey(
  key: readonly unknown[],
): readonly unknown[] | null {
  const identity = useAuthStore((state) => state.identity);
  const orgId = identity?.orgId ?? null;
  if (identity === null || orgId === null) {
    return null;
  }
  const segment: OrgScopeSegment = {
    scope: "org",
    userId: identity.userId,
    orgId,
  };
  return [...key, segment];
}

/**
 * Запрос user-scoped данных — того, что принадлежит пользователю, а не
 * организации: сам пользователь (`/me`), его сессии, список доступных ему
 * организаций. Смена активной организации такие данные не меняет.
 */
export function useUserScopedQuery<TData>(
  options: ScopedQueryOptions<TData>,
): UseQueryResult<TData, Error> {
  const identity = useAuthStore((state) => state.identity);
  const { key, enabled = true, ...rest } = options;
  const segment: UserScopeSegment | null =
    identity === null ? null : { scope: "user", userId: identity.userId };
  return useQuery<TData, Error, TData, readonly unknown[]>({
    ...rest,
    queryKey: [...key, segment],
    enabled: segment !== null && enabled,
  });
}

/**
 * Запрос данных, не принадлежащих никому: проба `healthz` на лэндинге.
 * Ключ остаётся как есть, запрос идёт и без identity.
 */
export function useUnscopedQuery<TData>(
  options: ScopedQueryOptions<TData>,
): UseQueryResult<TData, Error> {
  const { key, enabled = true, ...rest } = options;
  return useQuery<TData, Error, TData, readonly unknown[]>({
    ...rest,
    queryKey: key,
    enabled,
  });
}
